import React, { useRef, useState } from 'react';
import axios from 'axios';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Box,
    Button,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Icon,
    Input,
    Stack,
    Text,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { FiDownload, FiTrash2, FiUserCheck } from 'react-icons/fi';

// A client of the firm asks for a copy of their data (GDPR Art. 15/20) or for
// erasure (Art. 17). The firm decides whether the request applies; FinanSee
// carries it out across every table. See app/services/data_protection.py.
export const describeErasure = (result) => {
    const deleted = Object.values(result?.deleted || {}).reduce((sum, n) => sum + Number(n || 0), 0);
    return `${deleted} records deleted, ${result?.audit_entries_rewritten || 0} audit entries now point to ${result?.erasure_reference}.`;
};

const errorText = (error) => {
    const detail = error?.response?.data?.error?.detail || error?.response?.data?.detail;
    if (error?.response?.status === 404) return 'No records for this client ID.';
    return typeof detail === 'string' ? detail : 'The request could not be completed. Try again.';
};

const DataProtectionRequests = ({
    apiBaseUrl,
    token,
    cardBg = 'white',
    borderColor = 'gray.200',
    headingTextColor = 'gray.800',
    bodyTextColor = 'gray.600',
    mutedTextColor = 'gray.500',
}) => {
    const [customerId, setCustomerId] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [busy, setBusy] = useState(null);
    const [lastResult, setLastResult] = useState('');
    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = useRef();
    const toast = useToast();
    const id = customerId.trim();
    const path = `${apiBaseUrl}/content/data-protection/customers/${encodeURIComponent(id)}`;
    const headers = { Authorization: `Bearer ${token}` };

    const exportRecords = async () => {
        setBusy('export');
        try {
            const res = await axios.get(`${path}/export`, { headers });
            const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `client-${id}-records.json`;
            link.click();
            URL.revokeObjectURL(link.href);
            const total = Object.values(res.data.counts || {}).reduce((sum, n) => sum + n, 0);
            setLastResult(`Exported ${total} records for ${id}.`);
        } catch (error) {
            toast({ title: 'Export failed', description: errorText(error), status: 'error', isClosable: true });
        } finally {
            setBusy(null);
        }
    };

    const eraseRecords = async () => {
        setBusy('erase');
        try {
            const res = await axios.post(`${path}/erase`, { confirm_customer_id: confirmation.trim() }, { headers });
            setLastResult(`Client erased. ${describeErasure(res.data)}`);
            setCustomerId('');
            onClose();
        } catch (error) {
            toast({ title: 'Erasure failed', description: errorText(error), status: 'error', isClosable: true });
        } finally {
            setBusy(null);
            setConfirmation('');
        }
    };

    return (
        <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={{ base: 4, md: 5 }} boxShadow="sm">
            <HStack spacing={3} mb={2}>
                <Icon as={FiUserCheck} color="brand.navy" boxSize={5} />
                <Heading size="sm" color={headingTextColor}>Data-protection requests</Heading>
            </HStack>
            <Text fontSize="sm" color={bodyTextColor} mb={4}>
                When one of your clients asks for a copy of their data or to be erased, enter their client ID.
                You decide whether the request applies: record-keeping duties may require you to keep some records.
                Erasure removes the client from scores, queues, holdings, transactions and evidence; audit entries
                stay, with the client ID replaced by an erasure reference.
            </Text>
            <Stack direction={{ base: 'column', md: 'row' }} spacing={3} align={{ md: 'flex-end' }}>
                <FormControl maxW={{ md: '320px' }}>
                    <FormLabel fontSize="sm" color={bodyTextColor}>Client ID</FormLabel>
                    <Input value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="e.g. RB000263" />
                </FormControl>
                <Button leftIcon={<FiDownload />} onClick={exportRecords} isDisabled={!id} isLoading={busy === 'export'}>
                    Export records
                </Button>
                <Button leftIcon={<FiTrash2 />} colorScheme="red" variant="outline" onClick={onOpen} isDisabled={!id}>
                    Erase client
                </Button>
            </Stack>
            {lastResult && (
                <Text fontSize="sm" color={mutedTextColor} mt={3} role="status">{lastResult}</Text>
            )}

            <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">Erase client {id}?</AlertDialogHeader>
                        <AlertDialogBody>
                            <Text mb={3}>This cannot be undone. Type the client ID again to confirm.</Text>
                            <Input
                                aria-label="Confirm client ID"
                                value={confirmation}
                                onChange={(e) => setConfirmation(e.target.value)}
                            />
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onClose}>Cancel</Button>
                            <Button
                                colorScheme="red"
                                ml={3}
                                onClick={eraseRecords}
                                isDisabled={confirmation.trim() !== id}
                                isLoading={busy === 'erase'}
                            >
                                Erase permanently
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default DataProtectionRequests;
